package com.havn.app.features.leads.presentation

import androidx.compose.foundation.BorderStroke
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.itemsIndexed
import androidx.compose.foundation.lazy.rememberLazyListState
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.BasicTextField
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Brush
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import com.havn.app.core.designsystem.EmptyState
import com.havn.app.core.designsystem.HavnBackground
import com.havn.app.core.designsystem.HavnButton
import com.havn.app.core.designsystem.HavnButtonStyle
import com.havn.app.core.designsystem.HavnCard
import com.havn.app.core.designsystem.HavnCardBorder
import com.havn.app.core.designsystem.HavnGold
import com.havn.app.core.designsystem.HavnGoldLight
import com.havn.app.core.designsystem.HavnIcon
import com.havn.app.core.designsystem.HavnIconKey
import com.havn.app.core.designsystem.HavnPullToRefreshBox
import com.havn.app.core.designsystem.HavnIvory
import com.havn.app.core.designsystem.HavnLeadCard
import com.havn.app.core.designsystem.HavnListDefaults
import com.havn.app.core.designsystem.HavnListFilterChip
import com.havn.app.core.designsystem.HavnListRowSeparator
import com.havn.app.core.designsystem.HavnMetric
import com.havn.app.core.designsystem.HavnMuted
import com.havn.app.core.designsystem.HavnPushNavigation
import com.havn.app.core.designsystem.HavnScreenFrame
import com.havn.app.core.designsystem.HavnSearchField
import com.havn.app.core.designsystem.HavnStatus
import com.havn.app.core.designsystem.HavnStatusBadge
import com.havn.app.core.designsystem.HavnSurfaceElevated
import com.havn.app.core.designsystem.HavnTextMuted
import com.havn.app.core.designsystem.HavnTextPrimary
import com.havn.app.core.designsystem.HavnTextField
import com.havn.app.core.designsystem.HavnTopHeaderTextStyle
import com.havn.app.core.designsystem.LoadingState
import com.havn.app.core.designsystem.havnRefreshStartGate
import com.havn.app.core.localization.LocalHavnStrings
import com.havn.app.core.media.HavnDatabaseImage
import com.havn.app.core.platform.HavnExternalActions
import com.havn.app.features.followups.presentation.FollowUpComposerContent
import com.havn.app.features.followups.presentation.FollowUpsViewModel
import com.havn.app.features.leads.domain.Lead
import com.havn.app.features.leads.domain.LeadFollowUp
import com.havn.app.features.leads.domain.LeadPropertySource
import com.havn.app.features.openhouses.domain.LeadTemperature
import com.havn.app.ui.theme.LocalHavnThemeTokens

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun LeadsScreen(
    viewModel: LeadsViewModel,
    followUpsViewModel: FollowUpsViewModel,
    onSendFollowUp: (String) -> Unit = {},
    onFollowUpCompleted: () -> Unit = {},
    canUseAiFollowUps: Boolean = true,
    onAiUpgradeRequired: () -> Unit = {},
    detailBackLabel: String? = null,
    onBackFromDetail: (() -> Unit)? = null
) {
    val state by viewModel.uiState.collectAsState()
    val strings = LocalHavnStrings.current

    HavnPushNavigation(
        targetState = state.mode,
        depthFor = LeadsMode::navigationDepth
    ) { mode ->
        if (mode == LeadsMode.List) {
            LeadsListHome(
                state = state,
                viewModel = viewModel
            )
            return@HavnPushNavigation
        }

        LeadDetailHome(
            state = state,
            viewModel = viewModel,
            followUpsViewModel = followUpsViewModel,
            onSendFollowUp = onSendFollowUp,
            onFollowUpCompleted = onFollowUpCompleted,
            canUseAiFollowUps = canUseAiFollowUps,
            onAiUpgradeRequired = onAiUpgradeRequired,
            detailBackLabel = detailBackLabel,
            onBackFromDetail = onBackFromDetail
        )
    }
}

@Composable
@OptIn(ExperimentalMaterial3Api::class)
private fun LeadsListHome(
    state: LeadsUiState,
    viewModel: LeadsViewModel
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val listState = rememberLazyListState()
    var gestureStartedAtTop by remember { mutableStateOf(false) }
    HavnPullToRefreshBox(
        isRefreshing = state.isLoading,
        onRefresh = {
            if (gestureStartedAtTop) {
                viewModel.refresh()
            }
        },
        modifier = Modifier
            .fillMaxSize()
            .havnRefreshStartGate(listState) { gestureStartedAtTop = it }
            .background(Brush.verticalGradient(listOf(theme.gradientStart, theme.background, theme.gradientEnd)))
    ) {
        LazyColumn(
            state = listState,
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 18.dp, vertical = 18.dp),
            verticalArrangement = Arrangement.spacedBy(HavnListDefaults.headerToSearchGap)
        ) {
            item(key = "leads-header") {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically
                ) {
                    Text(
                        text = strings.navLeads,
                        modifier = Modifier.weight(1f),
                        color = theme.primaryText,
                        style = HavnTopHeaderTextStyle
                    )
                }
            }

            item(key = "leads-filters") {
                Column(verticalArrangement = Arrangement.spacedBy(HavnListDefaults.searchToFilterGap)) {
                    HavnSearchField(
                        value = state.searchQuery,
                        onValueChange = viewModel::updateSearchQuery,
                        placeholder = strings.searchLeadsPlaceholder,
                        modifier = Modifier.fillMaxWidth()
                    )

                    LeadListSegments(
                        selectedTemperature = state.temperatureFilter,
                        followUpSelected = state.followUpFilter,
                        onTemperatureSelected = viewModel::selectTemperatureFilter,
                        onFollowUpSelected = viewModel::selectFollowUpFilter
                    )
                }
            }

            state.error?.let { message ->
                item(key = "leads-error") {
                    Text(message, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodyMedium)
                }
            }

            when {
                state.isLoading && state.leads.isEmpty() -> {
                    items(8, key = { "lead-skeleton-$it" }) {
                        LeadCardSkeleton()
                    }
                }
                state.filteredLeads.isEmpty() -> {
                    item(key = "leads-empty") {
                        EmptyState(
                            title = strings.noLeadsFoundTitle,
                            body = if (state.leads.isEmpty()) strings.noLeadsEmptyBody else strings.noLeadsFilteredBody,
                            actionText = null,
                            onAction = null
                        )
                    }
                }
                else -> {
                    itemsIndexed(state.filteredLeads, key = { _, lead -> lead.id }) { index, lead ->
                        CompactLeadRow(
                            lead = lead,
                            onClick = { viewModel.openLead(lead.id) }
                        )
                        if (index < state.filteredLeads.lastIndex) {
                            HavnListRowSeparator()
                        }
                    }
                    if (state.hasMoreLeads) {
                        item(key = "leads-load-more") {
                            HavnButton(
                                text = "Load more",
                                onClick = viewModel::loadMoreLeads,
                                modifier = Modifier.fillMaxWidth(),
                                style = HavnButtonStyle.Secondary,
                                loading = state.isLoadingMore
                            )
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun LeadsListContent(
    state: LeadsUiState,
    viewModel: LeadsViewModel
) {
    val strings = LocalHavnStrings.current
    LeadFilters(
        searchQuery = state.searchQuery,
        selectedPropertyId = state.selectedPropertyId,
        selectedTemperature = state.temperatureFilter,
        properties = state.propertyFilters,
        onSearchChanged = viewModel::updateSearchQuery,
        onPropertySelected = viewModel::selectPropertyFilter,
        onTemperatureSelected = viewModel::selectTemperatureFilter
    )

    if (state.isLoading) {
        LoadingState(strings.loadingLeads)
    } else if (state.filteredLeads.isEmpty()) {
        EmptyState(
            title = strings.noLeadsFoundTitle,
            body = if (state.leads.isEmpty()) {
                strings.noLeadsEmptyBody
            } else {
                strings.noLeadsFilteredBody
            },
            actionText = null,
            onAction = null
        )
    } else {
        state.filteredLeads.forEach { lead ->
            HavnLeadCard(
                name = lead.fullName,
                contact = lead.sourceProperty.title,
                sourceProperty = lead.sourceOpenHouse.displayTitle,
                avatarDataUrl = lead.avatarDataUrl,
                temperature = lead.temperature.toStatus(),
                lastActivityTime = "${strings.commonCheckedIn} ${lead.checkedInAt.compactDateTime(strings)}",
                onClick = { viewModel.openLead(lead.id) }
            )
        }
    }
}

@Composable
private fun CompactLeadSearchField(
    value: String,
    onValueChange: (String) -> Unit,
    placeholder: String,
    modifier: Modifier = Modifier
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = modifier.height(42.dp),
        shape = RoundedCornerShape(10.dp),
        color = theme.surface.copy(alpha = 0.84f),
        border = BorderStroke(1.dp, theme.border.copy(alpha = 0.82f))
    ) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 12.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(8.dp)
        ) {
            HavnIcon(
                icon = HavnIconKey.Search,
                contentDescription = null,
                tint = theme.secondaryText,
                modifier = Modifier.size(17.dp)
            )
            BasicTextField(
                value = value,
                onValueChange = onValueChange,
                singleLine = true,
                textStyle = MaterialTheme.typography.bodyMedium.copy(color = theme.primaryText),
                cursorBrush = androidx.compose.ui.graphics.SolidColor(theme.primaryAccent),
                modifier = Modifier.weight(1f),
                decorationBox = { innerTextField ->
                    if (value.isBlank()) {
                        Text(
                            placeholder,
                            color = theme.secondaryText,
                            style = MaterialTheme.typography.bodyMedium,
                            maxLines = 1,
                            overflow = TextOverflow.Ellipsis
                        )
                    }
                    innerTextField()
                }
            )
        }
    }
}

@Composable
private fun LeadListSegments(
    selectedTemperature: LeadTemperature?,
    followUpSelected: Boolean,
    onTemperatureSelected: (LeadTemperature?) -> Unit,
    onFollowUpSelected: () -> Unit
) {
    val strings = LocalHavnStrings.current
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.spacedBy(HavnListDefaults.filterChipGap)
    ) {
        HavnListFilterChip(
            label = strings.commonAll,
            selected = selectedTemperature == null && !followUpSelected,
            onClick = { onTemperatureSelected(null) },
            modifier = Modifier.weight(0.78f)
        )
        LeadTemperature.entries.forEach { temperature ->
            HavnListFilterChip(
                label = temperature.localizedLabel(strings),
                selected = selectedTemperature == temperature && !followUpSelected,
                onClick = { onTemperatureSelected(temperature) },
                modifier = Modifier.weight(1f)
            )
        }
        HavnListFilterChip(
            label = strings.commonFollowUps.removeSuffix("s"),
            selected = followUpSelected,
            onClick = onFollowUpSelected,
            modifier = Modifier.weight(1.58f)
        )
    }
}

@Composable
private fun CompactLeadRow(
    lead: Lead,
    onClick: () -> Unit
) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        onClick = onClick,
        color = Color.Transparent,
        modifier = Modifier
            .fillMaxWidth()
            .height(58.dp)
    ) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 2.dp, vertical = 6.dp),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(10.dp)
        ) {
            LeadAvatar(lead.fullName, lead.avatarDataUrl)
            Column(
                modifier = Modifier.weight(1f),
                verticalArrangement = Arrangement.Center
            ) {
                Text(
                    text = lead.fullName,
                    color = theme.primaryText,
                    style = MaterialTheme.typography.bodyMedium,
                    fontWeight = FontWeight.SemiBold,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
                Text(
                    text = lead.sourceProperty.title,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodySmall,
                    maxLines = 1,
                    overflow = TextOverflow.Ellipsis
                )
            }
            Column(
                horizontalAlignment = Alignment.End,
                verticalArrangement = Arrangement.spacedBy(5.dp)
            ) {
                Text(
                    text = lead.checkedInAt.leadTime(),
                    color = theme.primaryText,
                    style = MaterialTheme.typography.labelSmall,
                    maxLines = 1
                )
                Row(horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                    if (lead.hasCompletedFollowUp) {
                        CompactFollowUpBadge()
                    }
                    CompactTemperatureBadge(lead)
                }
            }
        }
    }
}

@Composable
private fun LeadCardSkeleton() {
    val theme = LocalHavnThemeTokens.current
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .height(58.dp)
            .padding(horizontal = 2.dp, vertical = 6.dp),
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(10.dp)
    ) {
        Box(
            modifier = Modifier
                .size(38.dp)
                .clip(RoundedCornerShape(19.dp))
                .background(theme.surfaceElevated.copy(alpha = 0.85f))
        )
        Column(
            modifier = Modifier.weight(1f),
            verticalArrangement = Arrangement.spacedBy(7.dp)
        ) {
            HavnLeadSkeletonBlock(Modifier.fillMaxWidth(0.42f).height(14.dp))
            HavnLeadSkeletonBlock(Modifier.fillMaxWidth(0.62f).height(12.dp))
        }
        Column(
            horizontalAlignment = Alignment.End,
            verticalArrangement = Arrangement.spacedBy(7.dp)
        ) {
            HavnLeadSkeletonBlock(Modifier.width(72.dp).height(12.dp))
            HavnLeadSkeletonBlock(Modifier.width(92.dp).height(22.dp))
        }
    }
}

@Composable
private fun HavnLeadSkeletonBlock(modifier: Modifier = Modifier) {
    val theme = LocalHavnThemeTokens.current
    Box(
        modifier = modifier
            .clip(RoundedCornerShape(7.dp))
            .background(theme.surfaceElevated.copy(alpha = 0.82f))
    )
}

@Composable
private fun LeadAvatar(name: String, avatarDataUrl: String?) {
    val theme = LocalHavnThemeTokens.current
    val initials = name
        .split(" ")
        .filter { it.isNotBlank() }
        .take(2)
        .joinToString("") { it.first().uppercase() }
        .ifBlank { "H" }
    Box(
        modifier = Modifier
            .size(38.dp)
            .clip(RoundedCornerShape(19.dp))
            .background(Brush.linearGradient(listOf(theme.secondaryAccent.copy(alpha = 0.72f), theme.surface)))
            .border(1.dp, theme.primaryAccent.copy(alpha = 0.45f), RoundedCornerShape(19.dp)),
        contentAlignment = Alignment.Center
    ) {
        val rendered = HavnDatabaseImage(
            dataUrl = avatarDataUrl,
            contentDescription = name,
            modifier = Modifier.fillMaxSize(),
            contentScale = ContentScale.Crop
        )
        if (!rendered) {
            Text(
                text = initials,
                color = theme.primaryText,
                style = MaterialTheme.typography.labelLarge,
                fontWeight = FontWeight.Bold
            )
        }
    }
}

@Composable
private fun LargeLeadAvatar(name: String, avatarDataUrl: String?) {
    val theme = LocalHavnThemeTokens.current
    val initials = name
        .split(" ")
        .filter { it.isNotBlank() }
        .take(2)
        .joinToString("") { it.first().uppercase() }
        .ifBlank { "H" }
    Box(
        modifier = Modifier
            .size(76.dp)
            .clip(RoundedCornerShape(38.dp))
            .background(Brush.linearGradient(listOf(theme.secondaryAccent.copy(alpha = 0.75f), theme.surface)))
            .border(1.dp, theme.primaryAccent.copy(alpha = 0.52f), RoundedCornerShape(38.dp)),
        contentAlignment = Alignment.Center
    ) {
        val rendered = HavnDatabaseImage(
            dataUrl = avatarDataUrl,
            contentDescription = name,
            modifier = Modifier.fillMaxSize(),
            contentScale = ContentScale.Crop
        )
        if (!rendered) {
            Text(
                text = initials,
                color = theme.primaryText,
                style = MaterialTheme.typography.headlineSmall,
                fontWeight = FontWeight.Bold
            )
        }
    }
}

@Composable
private fun CompactTemperatureBadge(lead: Lead) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val label = lead.temperature.localizedLabel(strings)
    val color = when {
        lead.temperature == LeadTemperature.Hot -> Color(0xFFE85D4A)
        lead.temperature == LeadTemperature.Warm -> Color(0xFFD99A32)
        else -> Color(0xFF35C9D0)
    }
    Surface(
        shape = RoundedCornerShape(6.dp),
        color = color.copy(alpha = 0.9f),
        border = BorderStroke(1.dp, color.copy(alpha = 0.95f))
    ) {
        Text(
            text = label,
            color = if (lead.temperature == LeadTemperature.Cold) Color(0xFF031316) else theme.primaryText,
            style = MaterialTheme.typography.labelSmall,
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp),
            maxLines = 1
        )
    }
}

@Composable
private fun CompactFollowUpBadge() {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    Surface(
        shape = RoundedCornerShape(6.dp),
        color = theme.primaryAccent.copy(alpha = 0.9f),
        border = BorderStroke(1.dp, theme.secondaryAccent)
    ) {
        Text(
            text = strings.commonFollowUps.removeSuffix("s"),
            color = theme.background,
            style = MaterialTheme.typography.labelSmall,
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 4.dp),
            maxLines = 1
        )
    }
}

@Composable
private fun LeadFilters(
    searchQuery: String,
    selectedPropertyId: String?,
    selectedTemperature: LeadTemperature?,
    properties: List<LeadPropertySource>,
    onSearchChanged: (String) -> Unit,
    onPropertySelected: (String?) -> Unit,
    onTemperatureSelected: (LeadTemperature?) -> Unit
) {
    val strings = LocalHavnStrings.current
    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            HavnTextField(
                value = searchQuery,
                onValueChange = onSearchChanged,
                label = strings.searchLeads,
                placeholder = strings.searchLeadsPlaceholder,
                leadingIcon = HavnIconKey.Search
            )

            LeadTemperatureSegmentedControl(
                selectedTemperature = selectedTemperature,
                includeAll = true,
                onTemperatureSelected = onTemperatureSelected
            )

            HavnButton(
                text = strings.allProperties,
                onClick = { onPropertySelected(null) },
                style = if (selectedPropertyId == null) HavnButtonStyle.Primary else HavnButtonStyle.Secondary,
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = HavnIconKey.Properties
            )
            properties.forEach { property ->
                HavnButton(
                    text = property.title,
                    onClick = { onPropertySelected(property.id) },
                    style = if (selectedPropertyId == property.id) HavnButtonStyle.Primary else HavnButtonStyle.Ghost,
                    modifier = Modifier.fillMaxWidth(),
                    leadingIcon = HavnIconKey.Home
                )
            }

        }
    }
}

@Composable
private fun LeadDetailHome(
    state: LeadsUiState,
    viewModel: LeadsViewModel,
    followUpsViewModel: FollowUpsViewModel,
    onSendFollowUp: (String) -> Unit,
    onFollowUpCompleted: () -> Unit,
    canUseAiFollowUps: Boolean,
    onAiUpgradeRequired: () -> Unit,
    detailBackLabel: String?,
    onBackFromDetail: (() -> Unit)?
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val followUpsState by followUpsViewModel.uiState.collectAsState()
    val lead = state.selectedLead
    if (lead == null) {
        Box(
            modifier = Modifier
                .fillMaxSize()
                .background(Brush.verticalGradient(listOf(theme.gradientStart, theme.background, theme.gradientEnd)))
                .padding(20.dp),
            contentAlignment = Alignment.Center
        ) {
            EmptyState(
                title = strings.leadUnavailableTitle,
                body = strings.leadUnavailableBody,
                actionText = strings.backToLeads,
                onAction = viewModel::closeDetail
            )
        }
        return
    }

    LaunchedEffect(lead.id, lead.sourceProperty.photoDataUrls.size) {
        followUpsViewModel.openSendFollowUpForLead(lead)
    }

    LaunchedEffect(followUpsState.completedFollowUpEventId) {
        if (followUpsState.completedFollowUpEventId != null) {
            onFollowUpCompleted()
            followUpsViewModel.consumeCompletedFollowUpEvent()
        }
    }

    HavnScreenFrame(
        eyebrow = "",
        title = lead.fullName,
        subtitle = "",
        backLabel = detailBackLabel ?: strings.navLeads,
        onBack = onBackFromDetail ?: viewModel::closeDetail
    ) {
        Column(verticalArrangement = Arrangement.spacedBy(14.dp)) {
            if (state.isDetailLoading) {
                LoadingState("Loading lead details")
            }
            HavnCard {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(14.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        LargeLeadAvatar(lead.fullName, lead.avatarDataUrl)
                        Column(
                            modifier = Modifier.weight(1f),
                            verticalArrangement = Arrangement.spacedBy(5.dp)
                        ) {
                            Text(
                                text = lead.fullName,
                                color = theme.primaryText,
                                style = MaterialTheme.typography.headlineSmall,
                                fontWeight = FontWeight.SemiBold,
                                maxLines = 1,
                                overflow = TextOverflow.Ellipsis
                            )
                            lead.phone?.takeIf { it.isNotBlank() }?.let {
                                Text(it, color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge)
                            }
                            lead.email?.takeIf { it.isNotBlank() }?.let {
                                Text(it, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
                            }
                        }
                    }
                    DetailLine(strings.commonProperty, lead.sourceProperty.title)
                    DetailLine(strings.commonAddress, lead.sourceProperty.address)
                    DetailLine(strings.commonOpenHouse, lead.sourceOpenHouse.displayTitle)
                    DetailLine(strings.commonCheckedIn, lead.checkedInAt.compactDateTime(strings))
                    DetailLine(strings.leadFollowUp, lead.nextFollowUpLine(strings))
                }
            }

            HavnCard {
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    horizontalArrangement = Arrangement.spacedBy(10.dp)
                ) {
                    LeadContactActionButton(
                        label = strings.leadActionCall,
                        icon = HavnIconKey.Phone,
                        onClick = { lead.phone?.let(HavnExternalActions::openPhone) },
                        modifier = Modifier.weight(1f),
                        enabled = !lead.phone.isNullOrBlank()
                    )
                    LeadContactActionButton(
                        label = strings.leadActionSms,
                        icon = HavnIconKey.Message,
                        onClick = {
                            lead.phone?.let {
                                HavnExternalActions.openSms(
                                    to = it,
                                    body = "${strings.greetingHi} ${lead.firstName(strings)},"
                                )
                            }
                        },
                        modifier = Modifier.weight(1f),
                        enabled = !lead.phone.isNullOrBlank()
                    )
                    LeadContactActionButton(
                        label = strings.leadActionEmail,
                        icon = HavnIconKey.Mail,
                        onClick = {
                            lead.email?.let {
                                HavnExternalActions.openEmail(
                                    to = it,
                                    subject = "${strings.followUpDefaultSubject} ${lead.sourceProperty.title}",
                                    body = "${strings.greetingHi} ${lead.firstName(strings)},"
                                )
                            }
                        },
                        modifier = Modifier.weight(1f),
                        enabled = !lead.email.isNullOrBlank()
                    )
                }
            }

            HavnCard {
                Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
                    Text(strings.leadTemperature, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
                    LeadTemperatureSegmentedControl(
                        selectedTemperature = lead.temperature,
                        includeAll = false,
                        enabled = !state.isSaving,
                        onTemperatureSelected = { temperature ->
                            if (temperature != null) viewModel.setTemperature(temperature)
                        }
                    )
                }
            }

            FollowUpComposerContent(
                state = followUpsState,
                viewModel = followUpsViewModel,
                showLeadSummary = false,
                showAiAssistant = true,
                canUseAiAssistant = canUseAiFollowUps,
                onAiUpgradeRequired = onAiUpgradeRequired
            )

            state.error?.let { message ->
                Text(message, color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodyMedium)
            }
        }
    }
}

private fun Lead.firstName(strings: com.havn.app.core.localization.HavnStrings): String =
    fullName.substringBefore(" ").ifBlank { strings.leadGenericGreetingName }

@Composable
private fun LeadContactActionButton(
    label: String,
    icon: HavnIconKey,
    onClick: () -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true
) {
    val theme = LocalHavnThemeTokens.current
    val contentColor = if (enabled) theme.primaryAccent else theme.mutedText
    Surface(
        onClick = onClick,
        enabled = enabled,
        modifier = modifier.height(64.dp),
        shape = RoundedCornerShape(16.dp),
        color = theme.surface.copy(alpha = if (enabled) 0.74f else 0.42f),
        border = BorderStroke(1.dp, if (enabled) theme.primaryAccent.copy(alpha = 0.45f) else theme.border.copy(alpha = 0.55f))
    ) {
        Row(
            modifier = Modifier
                .fillMaxSize()
                .padding(horizontal = 8.dp),
            horizontalArrangement = Arrangement.Center,
            verticalAlignment = Alignment.CenterVertically
        ) {
            HavnIcon(icon, contentDescription = label, tint = contentColor, modifier = Modifier.size(19.dp))
            Spacer(Modifier.width(7.dp))
            Text(
                label,
                color = if (enabled) theme.primaryText else theme.mutedText,
                style = MaterialTheme.typography.labelLarge,
                fontWeight = FontWeight.SemiBold,
                maxLines = 1,
                overflow = TextOverflow.Clip
            )
        }
    }
}

@Composable
private fun DetailTemperatureBadge(temperature: LeadTemperature) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val color = when (temperature) {
        LeadTemperature.Hot -> Color(0xFFE85D4A)
        LeadTemperature.Warm -> Color(0xFFD99A32)
        LeadTemperature.Cold -> Color(0xFF35C9D0)
    }
    Surface(
        shape = RoundedCornerShape(7.dp),
        color = color.copy(alpha = 0.9f),
        border = BorderStroke(1.dp, color)
    ) {
        Text(
            text = "${temperature.localizedLabel(strings)} ${strings.leadLabelSuffix}",
            color = if (temperature == LeadTemperature.Cold) Color(0xFF031316) else theme.primaryText,
            style = MaterialTheme.typography.labelSmall,
            modifier = Modifier.padding(horizontal = 8.dp, vertical = 5.dp),
            maxLines = 1
        )
    }
}

@Composable
private fun LeadDetailPanel(content: @Composable ColumnScope.() -> Unit) {
    val theme = LocalHavnThemeTokens.current
    Surface(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        color = theme.surface.copy(alpha = 0.58f),
        border = BorderStroke(1.dp, theme.border.copy(alpha = 0.78f))
    ) {
        Column(
            modifier = Modifier.padding(horizontal = 12.dp, vertical = 12.dp),
            verticalArrangement = Arrangement.spacedBy(10.dp),
            content = content
        )
    }
}

@Composable
private fun LeadSection(
    title: String,
    body: String
) {
    val theme = LocalHavnThemeTokens.current
    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
        Text(
            title,
            color = theme.primaryAccent,
            style = MaterialTheme.typography.labelMedium,
            fontWeight = FontWeight.SemiBold
        )
        Text(
            body,
            color = theme.primaryText,
            style = MaterialTheme.typography.bodyMedium,
            maxLines = 4,
            overflow = TextOverflow.Ellipsis
        )
    }
}

@Composable
private fun LeadDivider() {
    val theme = LocalHavnThemeTokens.current
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .height(1.dp)
            .background(theme.border.copy(alpha = 0.62f))
    )
}

@Composable
private fun LeadsDetailContent(
    state: LeadsUiState,
    viewModel: LeadsViewModel
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val lead = state.selectedLead
    if (lead == null) {
        EmptyState(
            title = strings.leadUnavailableTitle,
            body = strings.leadUnavailableBody,
            actionText = strings.backToLeads,
            onAction = viewModel::closeDetail
        )
        return
    }

    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
            Row(horizontalArrangement = Arrangement.spacedBy(12.dp), modifier = Modifier.fillMaxWidth()) {
                Column(Modifier.weight(1f)) {
                    Text(lead.fullName, color = theme.primaryText, style = MaterialTheme.typography.headlineSmall)
                    Text(lead.contactLine, color = theme.secondaryText, style = MaterialTheme.typography.bodyLarge)
                }
                HavnStatusBadge(lead.temperature.toStatus())
            }
            DetailLine(strings.commonProperty, lead.sourceProperty.title)
            DetailLine(strings.commonAddress, lead.sourceProperty.address)
            DetailLine(strings.commonOpenHouse, lead.sourceOpenHouse.displayTitle)
            DetailLine(strings.commonCheckedIn, lead.checkedInAt.compactDateTime(strings))
            lead.intentScore?.let { DetailLine(strings.leadIntentScore, it.toString()) }
        }
    }

    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(strings.leadTemperature, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
            LeadTemperatureSegmentedControl(
                selectedTemperature = lead.temperature,
                includeAll = false,
                enabled = !state.isSaving,
                onTemperatureSelected = { temperature ->
                    if (temperature != null) viewModel.setTemperature(temperature)
                }
            )
        }
    }

    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(strings.privateAgentNotes, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
            HavnTextField(
                value = state.notesDraft,
                onValueChange = viewModel::updateNotesDraft,
                label = strings.notes,
                placeholder = strings.notesPlaceholder,
                singleLine = false,
                enabled = !state.isSaving
            )
            HavnButton(
                text = strings.saveNotes,
                onClick = viewModel::saveNotes,
                loading = state.isSaving,
                modifier = Modifier.fillMaxWidth(),
                leadingIcon = HavnIconKey.Check
            )
        }
    }

    HavnCard {
        Column(verticalArrangement = Arrangement.spacedBy(12.dp)) {
            Text(strings.followUpHistory, color = theme.primaryText, style = MaterialTheme.typography.titleLarge)
            if (lead.followUps.isEmpty()) {
                Text(
                    strings.noFollowUpsLogged,
                    color = theme.secondaryText,
                    style = MaterialTheme.typography.bodyLarge
                )
            } else {
                lead.followUps.forEach { followUp -> FollowUpRow(followUp) }
            }
        }
    }
}

@Composable
private fun DetailLine(label: String, value: String) {
    val theme = LocalHavnThemeTokens.current
    Column {
        Text(label, color = theme.primaryAccent, style = MaterialTheme.typography.labelLarge)
        Spacer(Modifier.height(3.dp))
        Text(
            value,
            color = theme.primaryText,
            style = MaterialTheme.typography.bodyLarge,
            maxLines = 2,
            overflow = TextOverflow.Ellipsis
        )
    }
}

@Composable
private fun LeadTemperatureSegmentedControl(
    selectedTemperature: LeadTemperature?,
    includeAll: Boolean,
    onTemperatureSelected: (LeadTemperature?) -> Unit,
    modifier: Modifier = Modifier,
    enabled: Boolean = true
) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    val segments = buildList {
        if (includeAll) add(LeadTemperatureSegment(strings.totalMetric, null))
        LeadTemperature.entries.forEach { temperature ->
            add(LeadTemperatureSegment(temperature.localizedLabel(strings), temperature))
        }
    }

    Surface(
        modifier = modifier.fillMaxWidth(),
        shape = RoundedCornerShape(16.dp),
        color = theme.surface,
        border = androidx.compose.foundation.BorderStroke(1.dp, theme.border)
    ) {
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .padding(3.dp),
            verticalAlignment = Alignment.CenterVertically
        ) {
            segments.forEachIndexed { index, segment ->
                val selected = selectedTemperature == segment.temperature
                val shape = when (index) {
                    0 -> RoundedCornerShape(topStart = 13.dp, bottomStart = 13.dp)
                    segments.lastIndex -> RoundedCornerShape(topEnd = 13.dp, bottomEnd = 13.dp)
                    else -> RoundedCornerShape(7.dp)
                }
                Box(
                    modifier = Modifier
                        .weight(1f)
                        .heightIn(min = 42.dp)
                        .clip(shape)
                        .background(if (selected) theme.primaryAccent else theme.surface)
                        .clickable(enabled = enabled) { onTemperatureSelected(segment.temperature) }
                        .padding(horizontal = 8.dp, vertical = 11.dp),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = segment.label,
                        color = when {
                            selected -> theme.background
                            enabled -> theme.primaryText
                            else -> theme.mutedText
                        },
                        style = MaterialTheme.typography.labelLarge,
                        maxLines = 1,
                        overflow = TextOverflow.Ellipsis
                    )
                }
                if (index != segments.lastIndex) {
                    Box(
                        modifier = Modifier
                            .width(1.dp)
                            .height(24.dp)
                            .background(theme.border)
                    )
                }
            }
        }
    }
}

private data class LeadTemperatureSegment(
    val label: String,
    val temperature: LeadTemperature?
)

@Composable
private fun FollowUpRow(followUp: LeadFollowUp) {
    val strings = LocalHavnStrings.current
    val theme = LocalHavnThemeTokens.current
    Column {
        Row(horizontalArrangement = Arrangement.spacedBy(8.dp), modifier = Modifier.fillMaxWidth()) {
            Text(
                followUp.channel.toSentenceCaseLabel(),
                color = theme.primaryAccent,
                style = MaterialTheme.typography.labelLarge
            )
            Text(
                followUp.status.toSentenceCaseLabel(),
                color = theme.secondaryText,
                style = MaterialTheme.typography.labelLarge
            )
            Spacer(Modifier.width(1.dp))
        }
        Text(
            followUp.subject?.takeIf { it.isNotBlank() } ?: strings.leadFollowUp,
            color = theme.primaryText,
            style = MaterialTheme.typography.titleMedium
        )
        followUp.body?.takeIf { it.isNotBlank() }?.let {
            Text(it, color = theme.secondaryText, style = MaterialTheme.typography.bodyMedium)
        }
        Text(
            (followUp.completedAt ?: followUp.dueAt ?: followUp.createdAt).orEmpty().compactDateTime(strings),
            color = theme.secondaryText,
            style = MaterialTheme.typography.labelLarge
        )
    }
}

private fun LeadTemperature.toStatus(): HavnStatus = when (this) {
    LeadTemperature.Hot -> HavnStatus.Hot
    LeadTemperature.Warm -> HavnStatus.Warm
    LeadTemperature.Cold -> HavnStatus.Cold
}

private fun LeadTemperature.localizedLabel(strings: com.havn.app.core.localization.HavnStrings): String = when (this) {
    LeadTemperature.Hot -> strings.commonHot
    LeadTemperature.Warm -> strings.commonWarm
    LeadTemperature.Cold -> strings.commonCold
}

private val Lead.preferredContactLine: String
    get() = phone?.takeIf { it.isNotBlank() }
        ?: email?.takeIf { it.isNotBlank() }
        ?: contactLine

private fun Lead.interestSummary(strings: com.havn.app.core.localization.HavnStrings): String =
    intentScore?.let { "${strings.leadIntentScore} $it ${strings.commonProperty.lowercase()} ${sourceProperty.title}" }
        ?: "${strings.leadInterestedIn} ${sourceProperty.title}"

private fun Lead.nextFollowUpLine(strings: com.havn.app.core.localization.HavnStrings): String =
    followUps
        .firstOrNull()
        ?.let { followUp ->
            val due = followUp.dueAt ?: followUp.completedAt ?: followUp.createdAt
            due?.leadReadableDateTime() ?: followUp.status.toSentenceCaseLabel()
        }
        ?: strings.leadNoFollowUpScheduled

private val Lead.hasCompletedFollowUp: Boolean
    get() = followUps.any { it.status == "completed" }

private fun String.toSentenceCaseLabel(): String =
    replace("_", " ")
        .lowercase()
        .replaceFirstChar { if (it.isLowerCase()) it.titlecase() else it.toString() }

private fun String.leadTime(): String {
    val compact = substringBefore(".")
        .substringBefore("+")
        .substringBefore("Z")
    val time = compact.substringAfter("T", compact)
        .substringAfter(" ", compact)
        .takeIf { ":" in it }
        ?: return compactDateTimeFallback()
    val hour = time.substringBefore(":").toIntOrNull() ?: return compactDateTimeFallback()
    val minute = time.substringAfter(":").take(2)
    val displayHour = when {
        hour == 0 -> 12
        hour > 12 -> hour - 12
        else -> hour
    }
    val suffix = if (hour >= 12) "PM" else "AM"
    return "$displayHour:$minute $suffix"
}

private fun String.leadReadableDateTime(): String {
    val compact = compactDateTimeFallback()
    val date = compact.substringBefore(" ")
    val timeSource = compact.substringAfter(" ", "")
    val time = timeSource.takeIf { ":" in it }?.let {
        val hour = it.substringBefore(":").toIntOrNull() ?: return@let it
        val minute = it.substringAfter(":").take(2)
        val displayHour = when {
            hour == 0 -> 12
            hour > 12 -> hour - 12
            else -> hour
        }
        val suffix = if (hour >= 12) "PM" else "AM"
        "$displayHour:$minute $suffix"
    }
    return if (time == null) compact else "$date at $time"
}

private fun String.compactDateTimeFallback(): String =
    replace("T", " ")
        .substringBefore(".")
        .substringBefore("+")
        .substringBefore("Z")

private fun String.compactDateTime(strings: com.havn.app.core.localization.HavnStrings): String {
    if (isBlank()) return strings.commonNotScheduled
    return replace("T", " ")
        .substringBefore(".")
        .substringBefore("+")
        .substringBefore("Z")
}

private fun LeadsMode.navigationDepth(): Int = when (this) {
    LeadsMode.List -> 0
    LeadsMode.Detail -> 1
}
